import { directSupabase } from '../lib/supabaseClient';

export const JOB_NOTIFICATION_TYPES = ['job_comment', 'job_completed', 'job_reopened'] as const;

export type JobNotificationType = (typeof JOB_NOTIFICATION_TYPES)[number];
export type JobNotificationAudience = 'office' | 'crew';

export type JobNotificationKind = 'comment' | 'completed' | 'reopened';

export function isJobNotificationType(type?: string | null): type is JobNotificationType {
  return JOB_NOTIFICATION_TYPES.includes(String(type || '') as JobNotificationType);
}

export function isOfficeJobViewerRole(role?: string | null): boolean {
  const next = String(role || 'user').trim().toLowerCase();
  return next === 'admin' || next === 'user';
}

export function isSiteCrewRole(role?: string | null): boolean {
  const next = String(role || '').trim().toLowerCase();
  return next === 'fitter' || next === 'labourer';
}

export function jobNotificationAudienceForRole(role?: string | null): JobNotificationAudience | null {
  if (isSiteCrewRole(role)) return 'office';
  if (isOfficeJobViewerRole(role)) return 'crew';
  return null;
}

export function jobNotificationLabel(type: string): string {
  if (type === 'job_comment') return 'Job comment';
  if (type === 'job_completed') return 'Job complete';
  if (type === 'job_reopened') return 'Job reopened';
  return 'Jobs';
}

export function jobNotificationPath(args: {
  viewerRole?: string | null;
  jobCardId?: string | null;
  projectId?: string | null;
}): string | null {
  const jobCardId = String(args.jobCardId || '').trim();
  const projectId = String(args.projectId || '').trim();
  if (isSiteCrewRole(args.viewerRole)) {
    return jobCardId ? `/job-records/${encodeURIComponent(jobCardId)}` : '/job-records';
  }
  if (projectId) return `/projects/${encodeURIComponent(projectId)}/workspace/implementation`;
  return jobCardId ? `/job-records/${encodeURIComponent(jobCardId)}` : null;
}

export function jobNotificationVisible(args: {
  viewerRole?: string | null;
  viewerId?: string | null;
  assignedJobCardIds?: string[];
  type?: string | null;
  actorId?: string | null;
  audience?: string | null;
  jobCardId?: string | null;
}): boolean {
  if (!isJobNotificationType(args.type)) return false;
  const viewerId = String(args.viewerId || '').trim();
  const actorId = String(args.actorId || '').trim();
  if (viewerId && actorId && viewerId === actorId) return false;
  if (isSiteCrewRole(args.viewerRole)) {
    if (String(args.audience || '') !== 'crew') return false;
    const jobCardId = String(args.jobCardId || '').trim();
    if (jobCardId && args.assignedJobCardIds && !args.assignedJobCardIds.includes(jobCardId)) {
      return false;
    }
    return true;
  }
  if (isOfficeJobViewerRole(args.viewerRole)) {
    return String(args.audience || '') === 'office';
  }
  return false;
}

function typeForKind(kind: JobNotificationKind): JobNotificationType {
  if (kind === 'completed') return 'job_completed';
  if (kind === 'reopened') return 'job_reopened';
  return 'job_comment';
}

function snippet(value?: string | null): string {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

export async function emitJobCardNotification(args: {
  kind: JobNotificationKind;
  jobCardId: string;
  projectId: string;
  actorId: string;
  comment?: string | null;
}): Promise<void> {
  const jobCardId = String(args.jobCardId || '').trim();
  const projectId = String(args.projectId || '').trim();
  const actorId = String(args.actorId || '').trim();
  if (!jobCardId || !projectId || !actorId) return;

  const [actorRes, cardRes, projectRes, assigneeRes] = await Promise.all([
    directSupabase.from('profiles').select('id, full_name, email, role').eq('id', actorId).maybeSingle(),
    (directSupabase as any).from('project_job_cards').select('id, title').eq('id', jobCardId).maybeSingle(),
    directSupabase.from('projects').select('id, project_name').eq('id', projectId).maybeSingle(),
    (directSupabase as any).from('project_job_assignees').select('profile_id').eq('job_card_id', jobCardId),
  ]);

  if (actorRes.error || !actorRes.data) return;
  const actor = actorRes.data as {
    full_name?: string | null;
    email?: string | null;
    role?: string | null;
  };
  const audience = jobNotificationAudienceForRole(actor.role);
  if (!audience) return;

  const actorName = String(actor.full_name || actor.email || 'Someone').trim();
  const jobTitle = String((cardRes.data as { title?: string } | null)?.title || 'Job card').trim();
  const projectName = String((projectRes.data as { project_name?: string } | null)?.project_name || '').trim();
  const where = projectName ? `${jobTitle} · ${projectName}` : jobTitle;
  const commentBit = snippet(args.comment);
  const message = args.kind === 'completed'
    ? `${actorName} marked ${where} complete`
    : args.kind === 'reopened'
      ? `${actorName} reopened ${where}`
      : (commentBit ? `${actorName} commented on ${where}: ${commentBit}` : `${actorName} commented on ${where}`);

  const assigneeIds = ((assigneeRes.data || []) as Array<{ profile_id?: string }>)
    .map((row) => String(row.profile_id || '').trim())
    .filter(Boolean);

  const { error } = await directSupabase.from('notifications').insert({
    type: typeForKind(args.kind),
    message,
    metadata: {
      jobCardId,
      projectId,
      projectName: projectName || null,
      jobTitle,
      actorId,
      actorName,
      actorRole: actor.role || null,
      audience,
      assigneeIds,
    },
  });
  if (error) throw new Error(error.message);
}

export function emitJobCardNotificationBackground(args: {
  kind: JobNotificationKind;
  jobCardId: string;
  projectId: string;
  actorId: string;
  comment?: string | null;
}): void {
  void emitJobCardNotification(args).catch((error) => {
    console.warn('Failed to emit job notification', error);
  });
}

export async function listAssignedJobCardIds(profileId: string): Promise<string[]> {
  const id = String(profileId || '').trim();
  if (!id) return [];
  const { data, error } = await (directSupabase as any)
    .from('project_job_assignees')
    .select('job_card_id')
    .eq('profile_id', id);
  if (error) throw new Error(error.message);
  return Array.from(new Set(
    ((data || []) as Array<{ job_card_id?: string }>)
      .map((row) => String(row.job_card_id || '').trim())
      .filter(Boolean)
  ));
}
